import { SelectField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { IOptionItem } from '@wyz/types';
import { Button, Flex, Icon, IconButton, Title, Tooltip } from '@wyz/ui';
import { serialize } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { Fragment } from 'react';
import { Card } from '@wyz/ui';
import { Link, useNavigate } from 'react-router';
import { useManageParameters } from '~/common/api';
import { IUserCustomer } from '~/common/types';
import { QuoteCartItem } from '~/purchase/components';
import { QuoteHelpers } from '~/purchase/helpers';
import { RIM_TYPE_ENUM } from '~/purchase/schemas';
import {
  IQuoteCart,
  IQuoteCartAction,
  IQuoteSetting,
  IRimType,
} from '~/purchase/types';

type Props = {
  cart: IQuoteCart;
  searchEnabled: true | false;
  actions: IQuoteCartAction;
  customer: IUserCustomer;
  settings: IQuoteSetting;
};

function ProductsForm({
  customer,
  cart,
  actions,
  searchEnabled = true,
  settings,
}: Props) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const manageParameters = useManageParameters({
    getSeasons: {
      enabled: true,
    },
  });
  return (
    <>
      <Flex justifyContent='between' alignItems='center'>
        <Flex className='gap-2 pb-3' alignItems='center'>
          <Title heading='lg' weight='bold' color='secondary'>
            <Trans i18nKey='app.common.product.tyres' />
          </Title>
          {!isNil(cart.rimType) && (
            <SelectField<false, IOptionItem<IRimType>>
              onSelect={(option) =>
                option?.value &&
                actions.updateRimType.process({ rimType: option.value })
              }
              name='rimType'
              options={[
                {
                  label: t('app.quote.rim_type.steel'),
                  value: RIM_TYPE_ENUM.STEEL,
                },
                {
                  label: t('app.quote.rim_type.alloy'),
                  value: RIM_TYPE_ENUM.ALLOY,
                },
              ]}
            />
          )}
        </Flex>
        {searchEnabled && (
          <IconButton
            id='new-search'
            iconSize='xs'
            icon='search'
            variant='tertiary-blue'
            onClick={() => void navigate('../search')}
          >
            <Trans i18nKey='app.common.button.new_search' />
          </IconButton>
        )}
      </Flex>
      <div className='row g-3'>
        {cart.groups.map((group, index) => (
          <Fragment key={index}>
            {group.items.map((item) => (
              <div key={item.id} className='col-12 col-lg-6'>
                <QuoteCartItem
                  customer={customer}
                  item={{
                    ...item,
                    bfDiscount: isNil(item.tyre.bf)
                      ? null
                      : QuoteHelpers.calculateBfDiscount(
                          item.tyre.bf,
                          QuoteHelpers.getPriceTaxExcl(
                            item.unitPriceTaxIncl,
                            cart.vatPercentage,
                          ),
                        ),
                  }}
                  vatPercentage={cart.vatPercentage}
                  group={group}
                  actions={actions}
                  showBfDiscount={settings.showBfDiscount ?? true}
                />
              </div>
            ))}
          </Fragment>
        ))}
        {searchEnabled &&
          cart.referenceCount < QuoteHelpers.QUOTE_CART_MAX_REFERENCE_COUNT &&
          (cart.dimensionCount === 1 ||
            cart.dimensionCount !==
              QuoteHelpers.QUOTE_CART_MAX_DIMENSION_COUNT) && (
            <div key='add' className='col-12 col-lg-6'>
              <Card className='h-100'>
                <Flex direction='column' className='h-100'>
                  <div className='d-flex flex-column align-items-center justify-content-center py-5 border-bottom border-1'>
                    <Button
                      id='add-same-size'
                      variant='tertiary-blue'
                      as={Link}
                      to={`/quotes/search?${serialize(
                        QuoteHelpers.getQuoteSearchQs(
                          cart,
                          manageParameters.getSeasons.data?.data,
                        ),
                      )}`}
                    >
                      <Flex direction='column' alignItems='center'>
                        <Icon icon='add-line' size='lg' />
                        <span>
                          <Trans i18nKey='app.purchase.quote_cart_page.add_same_dimension' />
                        </span>
                      </Flex>
                    </Button>
                  </div>
                  <div className='d-flex flex-column align-items-center justify-content-center py-5'>
                    <Tooltip
                      label={
                        <Trans i18nKey='app.purchase.quote_cart_page.cannot_add_dimension' />
                      }
                      enabled={
                        cart.referenceCount > 1 && cart.dimensionCount === 1
                      }
                    >
                      <div>
                        <Button
                          id='add-same-size'
                          variant='tertiary-blue'
                          disabled={
                            cart.referenceCount > 1 && cart.dimensionCount === 1
                          }
                          as={Link}
                          to='../search'
                        >
                          <Flex direction='column' alignItems='center'>
                            <Icon icon='add-line' size='lg' />

                            <span>
                              <Trans i18nKey='app.purchase.quote_cart_page.add_another_dimension' />
                            </span>
                          </Flex>
                        </Button>
                      </div>
                    </Tooltip>
                  </div>
                </Flex>
              </Card>
            </div>
          )}
      </div>
    </>
  );
}

export default ProductsForm;
